import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DashboardPage from "./pages/DashboardPage";
import CreateSetPage from "./pages/CreateSetPage";
import EditSetPage from "./pages/EditSetPage";
import StudyPage from "./pages/StudyPage";
import LearnModePage from "./pages/LearnModePage";
import MultipleChoicePage from "./pages/MultipleChoicePage";
import FlashcardReviewPage from "./pages/FlashcardReviewPage";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/signup" 
                element={
                    <PublicRoute>
                        <SignupPage />
                    </PublicRoute>
                } 
            />

            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/create" 
                element={
                    <ProtectedRoute>
                        <CreateSetPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/edit/:setId" 
                element={
                    <ProtectedRoute>
                        <EditSetPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/study/:setId" 
                element={
                    <ProtectedRoute>
                        <StudyPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/study/:setId/learn" 
                element={
                    <ProtectedRoute>
                        <LearnModePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/study/:setId/multiple-choice" 
                element={
                    <ProtectedRoute>
                        <MultipleChoicePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/study/:setId/flashcards" 
                element={
                    <ProtectedRoute>
                        <FlashcardReviewPage />
                    </ProtectedRoute>
                } 
            />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        toastOptions={{ 
                            className: 'rounded-none border-border font-sans' 
                        }} 
                    />
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
